import {
    CalendarMonth,
    Home,
    Logout,
    Notifications,
    Person,
    Tune,
} from '@mui/icons-material'
import {
    Avatar,
    Badge,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    Typography,
} from '@mui/material'
import List from '@mui/material/List'
import { candidateRouteConfig } from 'candidate/routes'
import { useSelector } from 'react-redux'
import { Link, NavLink, useLocation } from 'react-router-dom'
import WhiteLogo from 'shared/components/WhiteLogo'
import useIsMobile from 'shared/hooks/useIsMobile'
import { useNotifications } from 'shared/hooks/useNotification'
import { commonRouteConfig } from 'shared/routeConfig'
import { selectUser } from 'shared/state/userSlice'
import { getFullName, getInitials, getPxAsRem } from 'shared/utils'
import {
    AvatarContainer,
    LogoutContainer,
    MobileLogoContainer,
    SkipNavButton,
    StyledAppBar,
    StyledLogoContainer,
    StyledMenuItem,
    StyledMenuList,
    StyledNav,
    StyledNavLink,
} from './Styles'

const Sidebar = () => {
    const { firstName, lastName, nickname, profileImageKey } =
        useSelector(selectUser)
    const { notifications } = useNotifications()
    const isMobile = useIsMobile()
    const location = useLocation()

    const unreadNotifications = notifications.filter(
        (notification) => !notification.isRead
    )

    return (
        <>
            <SkipNavButton href="#main" isMobile={isMobile}>
                Skip Navigation
            </SkipNavButton>

            {isMobile ? (
                <StyledAppBar position="static">
                    <MobileLogoContainer
                        to={candidateRouteConfig.dashboard.path}
                    >
                        <WhiteLogo />
                    </MobileLogoContainer>
                    <List sx={{ display: 'flex', padding: 0 }}>
                        <ListItem disablePadding>
                            <ListItemButton
                                aria-label="log out"
                                sx={{ padding: getPxAsRem(8) }}
                                to={commonRouteConfig.logout.path}
                                component={Link}
                            >
                                <ListItemIcon>
                                    <Logout />
                                </ListItemIcon>
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton
                                aria-label="notifications"
                                sx={{ padding: getPxAsRem(8) }}
                                to={candidateRouteConfig.notifications.path}
                                component={Link}
                            >
                                <ListItemIcon
                                    classes={
                                        location.pathname ===
                                        candidateRouteConfig.notifications.path
                                            ? { root: 'active' }
                                            : { root: '' }
                                    }
                                >
                                    <Badge
                                        variant="dot"
                                        invisible={
                                            unreadNotifications.length < 1
                                        }
                                        color="primary"
                                    >
                                        <Notifications />
                                    </Badge>
                                </ListItemIcon>
                            </ListItemButton>
                        </ListItem>
                    </List>
                </StyledAppBar>
            ) : (
                <StyledNav>
                    <StyledLogoContainer>
                        <WhiteLogo data-testid="logo" />
                        <div>
                            <IconButton
                                component={NavLink}
                                to={candidateRouteConfig.notifications.path}
                                aria-label="notifications"
                            >
                                <Badge
                                    variant="dot"
                                    invisible={unreadNotifications.length < 1}
                                    color="primary"
                                >
                                    <Notifications />
                                </Badge>
                            </IconButton>
                        </div>
                    </StyledLogoContainer>

                    <StyledMenuList>
                        <StyledMenuItem disableGutters>
                            <StyledNavLink
                                to={candidateRouteConfig.dashboard.path}
                                tabIndex={-1}
                            >
                                <Home />
                                <Typography variant="body1">Home</Typography>
                            </StyledNavLink>
                        </StyledMenuItem>

                        <StyledMenuItem disableGutters>
                            <StyledNavLink
                                to={candidateRouteConfig.schedule.path}
                                tabIndex={-1}
                            >
                                <CalendarMonth />
                                <Typography variant="body1">
                                    Schedule
                                </Typography>
                            </StyledNavLink>
                        </StyledMenuItem>

                        <StyledMenuItem disableGutters>
                            <StyledNavLink
                                to={candidateRouteConfig.profile.path}
                                tabIndex={-1}
                            >
                                <Person />
                                <Typography variant="body1">Profile</Typography>
                            </StyledNavLink>
                        </StyledMenuItem>

                        <StyledMenuItem disableGutters>
                            <StyledNavLink
                                to={candidateRouteConfig.preferences.path}
                                tabIndex={-1}
                            >
                                <Tune />
                                <Typography variant="body1">
                                    Preferences
                                </Typography>
                            </StyledNavLink>
                        </StyledMenuItem>
                    </StyledMenuList>

                    <LogoutContainer>
                        <AvatarContainer>
                            <Avatar
                                data-testid="avatar"
                                src={profileImageKey || undefined}
                            >
                                {getInitials({ firstName, nickname, lastName })}
                            </Avatar>
                            <Typography variant="subtitle2">
                                {getFullName({ firstName, nickname, lastName })}
                            </Typography>
                        </AvatarContainer>

                        <IconButton href="/logout" aria-label="sign out">
                            <Logout />
                        </IconButton>
                    </LogoutContainer>
                </StyledNav>
            )}
        </>
    )
}

export default Sidebar
